import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { editAppRecordCellsById } from '@/lib/app-records';
import { rerunAppRecordById } from '@/lib/app-rerun';
import type { DashboardChatAppRecordResult } from '@shared/types/dashboard-chat';

/**
 * F6: what the app chat proposes on one object: corrected values (before →
 * after, with their reason) and/or a rerun of its analysis. Nothing is written
 * until the person applies it; a correction goes to the object's history.
 */
export const AppChatAppRecordCard = ({ result }: { result: DashboardChatAppRecordResult }) => {
  const [state, setState] = useState<{ edits: 'idle' | 'busy' | 'done'; rerun: 'idle' | 'busy' | 'done'; error: string | null }>({ edits: 'idle', rerun: 'idle', error: null });

  const applyEdits = () => {
    setState((current) => ({ ...current, edits: 'busy', error: null }));
    const cells = Object.fromEntries(result.edits.map((edit) => [edit.key, edit.value]));
    void editAppRecordCellsById(result.recordId, result.expectedVersion, cells, 'chat', result.edits.map((edit) => edit.reason).filter(Boolean).join(' · ') || null)
      .then(() => setState((current) => ({ ...current, edits: 'done' })))
      .catch((cause) => setState((current) => ({ ...current, edits: 'idle', error: cause instanceof Error ? cause.message : 'Correction non enregistrée.' })));
  };
  const rerun = () => {
    if (!result.flowId) return;
    setState((current) => ({ ...current, rerun: 'busy', error: null }));
    void rerunAppRecordById(result.flowId, result.recordId)
      .then(() => setState((current) => ({ ...current, rerun: 'done' })))
      .catch((cause) => setState((current) => ({ ...current, rerun: 'idle', error: cause instanceof Error ? cause.message : 'L’analyse n’a pas pu être relancée.' })));
  };

  return (
    <div className="mt-3 space-y-3 rounded-2xl border border-domino-cardBorder bg-domino-white px-3 py-3 shadow-sm">
      {result.edits.length > 0 && (
        <div>
          <p className="text-[11px] font-medium text-domino-aubergine">Corrections proposées</p>
          <ul className="mt-2 space-y-2">
            {result.edits.map((edit) => (
              <li key={edit.key} className="text-xs leading-5">
                <span className="font-semibold text-domino-ink">{edit.label}</span>
                <span className="block text-domino-inkSoft line-through">{edit.before ?? '(vide)'}</span>
                <span className="block text-domino-ink">{edit.value ?? '(valeur de Domino)'}</span>
                {edit.reason && <span className="block text-domino-inkSoft">{edit.reason}</span>}
              </li>
            ))}
          </ul>
          {state.edits === 'done'
            ? <p role="status" className="mt-2 text-xs text-emerald-700">Corrigé. La correction est dans l’historique de l’objet et s’annule depuis sa page.</p>
            : <Button size="sm" className="mt-2" disabled={state.edits === 'busy'} onClick={applyEdits}>{state.edits === 'busy' ? 'Enregistrement…' : 'Appliquer'}</Button>}
        </div>
      )}
      {result.rerun && result.flowId && (
        <div>
          <p className="text-[11px] font-medium text-domino-aubergine">Relancer l’analyse</p>
          {state.rerun === 'done'
            ? <p role="status" className="mt-1 text-xs text-domino-inkSoft">Analyse relancée : l’objet se met à jour à la fin. Les corrections sont gardées.</p>
            : <Button size="sm" variant="secondary" className="mt-2" disabled={state.rerun === 'busy'} onClick={rerun}>{state.rerun === 'busy' ? 'Relance…' : 'Relancer l’analyse'}</Button>}
        </div>
      )}
      {result.sources.length > 0 && (
        <div>
          <p className="text-[11px] font-medium text-domino-inkSoft">Sources</p>
          <ul className="mt-1 space-y-1">
            {result.sources.map((source) => (
              <li key={source.url} className="truncate text-xs"><a className="text-domino-aubergine underline" href={source.url} target="_blank" rel="noreferrer">{source.title}</a></li>
            ))}
          </ul>
        </div>
      )}
      {state.error && <p role="alert" className="text-xs text-red-700">{state.error}</p>}
    </div>
  );
};
